import { FileText, Maximize2, Minimize2, Paperclip, Send, X } from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  formatBytes,
  MAIL_ATTACHMENT_ACCEPT,
  MAIL_ATTACHMENT_MAX_TOTAL_BYTES,
  totalAttachmentBytes,
} from '../mailAttachments'
import type { MailBoxApi } from '../hooks/useMailBox'
import { MailRichEditor } from './MailRichEditor'
import { RecipientInput } from './RecipientInput'

type Props = { box: MailBoxApi }

export function MailCompose({ box }: Props) {
  if (!box.compose.open) return null
  // Oturum anahtarı: otomatik taslak kaydı (draftId/mode değişimi) pencereyi yeniden kurmaz.
  return <MailComposePanel key={box.compose.session} box={box} />
}

function MailComposePanel({ box }: Props) {
  const { t } = useTranslation()
  const { compose } = box
  const [expanded, setExpanded] = useState(false)
  const [showCc, setShowCc] = useState(Boolean(compose.cc.trim()))
  const [showBcc, setShowBcc] = useState(Boolean(compose.bcc.trim()))
  const fileRef = useRef<HTMLInputElement>(null)
  const attachments = compose.attachments ?? []
  const usedBytes = totalAttachmentBytes(attachments)

  const title =
    compose.mode === 'reply' || compose.mode === 'replyAll'
      ? t('mail.mailbox.composeReply')
      : compose.mode === 'forward'
        ? t('mail.mailbox.composeForward')
        : compose.mode === 'draft'
          ? t('mail.mailbox.composeDraft')
          : t('mail.mailbox.compose')

  return (
    <div
      className={cn(
        'fixed z-40 flex flex-col overflow-hidden border border-border bg-card shadow-2xl',
        expanded
          ? 'inset-4 rounded-xl sm:inset-8'
          : 'inset-x-3 bottom-3 max-h-[min(85dvh,40rem)] rounded-2xl sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-[min(36rem,calc(100vw-2.5rem))]',
      )}
    >
      <div className="flex items-center gap-2 bg-primary px-4 py-2.5 text-primary-foreground">
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium">{title}</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
          aria-label={expanded ? t('mail.mailbox.composeCollapse') : t('mail.mailbox.composeExpand')}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <Minimize2 /> : <Maximize2 />}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"
          aria-label={t('common.close')}
          onClick={box.closeCompose}
        >
          <X />
        </Button>
      </div>

      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault()
          void (async () => {
            if (!(await box.sendMail())) {
              toast.error(t('mail.mailbox.composeToRequired'))
              return
            }
            toast.success(t('mail.mailbox.sent'))
          })()
        }}
      >
        <div className="shrink-0 divide-y divide-border border-b border-border">
          <FieldRow label={t('mail.mailbox.to')}>
            <RecipientInput
              id="mail-to"
              value={compose.to}
              onChange={(to) => box.patchCompose({ to })}
              autoFocus={compose.mode === 'new' || compose.mode === 'forward'}
              placeholder={t('mail.mailbox.toPlaceholder')}
            />
            {!showCc || !showBcc ? (
              <div className="flex shrink-0 gap-0.5">
                {!showCc ? (
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowCc(true)}>
                    {t('mail.mailbox.cc')}
                  </Button>
                ) : null}
                {!showBcc ? (
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowBcc(true)}>
                    {t('mail.mailbox.bcc')}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </FieldRow>
          {showCc ? (
            <FieldRow label={t('mail.mailbox.cc')}>
              <RecipientInput
                id="mail-cc"
                value={compose.cc}
                onChange={(cc) => box.patchCompose({ cc })}
                autoFocus={!compose.cc}
                placeholder={t('mail.mailbox.ccPlaceholder')}
              />
            </FieldRow>
          ) : null}
          {showBcc ? (
            <FieldRow label={t('mail.mailbox.bcc')}>
              <RecipientInput
                id="mail-bcc"
                value={compose.bcc}
                onChange={(bcc) => box.patchCompose({ bcc })}
                autoFocus={!compose.bcc}
                placeholder={t('mail.mailbox.bccPlaceholder')}
              />
            </FieldRow>
          ) : null}
          <FieldRow label={t('mail.mailbox.subject')}>
            <Input
              id="mail-subject"
              value={compose.subject}
              onChange={(e) => box.patchCompose({ subject: e.target.value })}
              autoComplete="off"
              className="h-9 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              placeholder={t('mail.mailbox.subjectPlaceholder')}
            />
          </FieldRow>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-none p-3 sm:p-4">
          <MailRichEditor
            id="mail-body"
            value={compose.body}
            onChange={(html) => box.patchCompose({ body: html })}
            minHeight={expanded ? '20rem' : '14rem'}
            aria-label={t('mail.mailbox.body')}
          />
          {attachments.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {attachments.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-xs"
                >
                  <Paperclip className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate font-medium">{a.name}</span>
                  <span className="shrink-0 text-muted-foreground">{formatBytes(a.size)}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label={t('mail.mailbox.attachments.remove')}
                    onClick={() => box.removeAttachment(a.id)}
                  >
                    <X className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">{t('mail.mailbox.composeHint')}</p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-3 sm:px-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={MAIL_ATTACHMENT_ACCEPT}
              className="sr-only"
              onChange={(e) => {
                if (e.target.files?.length) box.addAttachments(e.target.files)
                e.target.value = ''
              }}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Paperclip />
              {t('mail.mailbox.attachments.add')}
            </Button>
            <span className="text-[11px] text-muted-foreground">
              {t('mail.mailbox.attachments.quota', {
                used: formatBytes(usedBytes),
                max: formatBytes(MAIL_ATTACHMENT_MAX_TOTAL_BYTES),
              })}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                box.saveDraft()
                toast.success(t('mail.mailbox.draftSaved'))
              }}
            >
              <FileText />
              {t('mail.mailbox.saveDraft')}
            </Button>
          </div>
          <Button type="submit" size="sm" className="min-w-28">
            <Send />
            {t('mail.mailbox.send')}
          </Button>
        </div>
      </form>
    </div>
  )
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 px-4 py-1.5">
      <span className="flex h-9 w-12 shrink-0 items-center text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
    </div>
  )
}
